import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Swords, User, Settings, Crown, Globe, BookOpen, ChevronRight, Library, Heart, type LucideIcon,
} from 'lucide-react';
import { useAppSelector } from '@/store/hooks';
import { apiFetch } from '@/lib/api';
import type { World, Campaign } from '../../../../backend/shared/types/index';

interface NavItem { to: string; icon: LucideIcon; label: string }
interface ChildItem { to: string; label: string }

interface WorldEntry { world: World; campaigns: Campaign[] }

// Library of Nix child links (always-visible under the Library group header)
const LIBRARY_CHILDREN: ChildItem[] = [
  { to: '/library',                   label: 'Documents'         },
  { to: '/boards',                    label: 'Message Board'     },
  { to: '/library/website-resources', label: 'Website Resources' },
];

const DM_ITEMS: NavItem[] = [
  { to: '/dm', icon: Crown, label: 'DM Dashboard' },
];

const ADMIN_ITEMS: NavItem[] = [
  { to: '/admin', icon: Settings, label: 'Admin Panel' },
];

// Child links shown indented under "Support Darkfeld"
const DONATE_CHILDREN: ChildItem[] = [
  { to: '/donate',            label: 'Donate'     },
  { to: '/donate/supporters', label: 'Supporters' },
];

// Child links shown indented under Characters
const CHARACTER_CHILDREN: ChildItem[] = [
  { to: '/characters/new', label: 'New Character' },
];

// Child links shown indented under Admin Panel (use ?tab= for deep-linking)
const ADMIN_CHILDREN: ChildItem[] = [
  { to: '/admin?tab=users',    label: 'Users'    },
  { to: '/admin?tab=worlds',   label: 'Worlds'   },
  { to: '/admin?tab=feedback', label: 'Feedback' },
];

// Small dot colour per campaign status
const STATUS_DOT: Record<string, string> = {
  active:    'bg-success',
  on_hiatus: 'bg-gold/60',
  completed: 'bg-parchment-muted/30',
};

// Sub-tabs for each campaign, shown when a campaign row is expanded in the sidebar
const CAMPAIGN_TABS: { id: string; label: string }[] = [
  { id: 'overview',     label: 'Overview'     },
  { id: 'resources',    label: 'Resources'    },
  { id: 'marketplaces', label: 'Marketplaces' },
  { id: 'characters',   label: 'Characters'   },
  { id: 'combat',       label: 'Modifiers'    },
  { id: 'lootchest',    label: 'Loot Chest'   },
  { id: 'inventory',    label: 'Inventory'    },
];

export default function Sidebar() {
  const { user, viewAsRole, viewAsUser } = useAppSelector((s) => s.auth);
  const location = useLocation();
  const current     = location.pathname;
  // Full path + query string — used to detect active admin sub-tabs
  const currentFull = location.pathname + location.search;

  // viewAsUser takes precedence; falls back to viewAsRole, then the real role.
  const effectiveRole = viewAsUser?.role ?? viewAsRole ?? user?.role;
  const isDmOrAdmin = effectiveRole === 'dm' || effectiveRole === 'admin';
  // Admin Panel only visible when the effective role is admin — so it correctly
  // disappears when previewing as a DM or player. Exit preview via the top banner.
  const isAdmin     = effectiveRole === 'admin';

  return (
    <aside className="w-52 shrink-0 hidden lg:block self-start sticky top-[3.75rem]">
      <div className="bg-darkbg-card border border-darkbg-border rounded-lg overflow-hidden
                      flex flex-col max-h-[calc(100vh-4rem)]">
        {/* Header strip */}
        <div className="px-4 py-3 border-b border-darkbg-border bg-gradient-to-r
                        from-darkbg-tertiary to-darkbg-card shrink-0">
          <p className="text-gold/70 text-xs font-display uppercase tracking-widest select-none">
            Navigation
          </p>
        </div>

        {/* Scrollable nav — fills remaining space between header and donate footer */}
        <nav className="p-2 space-y-0.5 overflow-y-auto flex-1">
          {/* Library of Nix — top-level group; always visible */}
          <SidebarLink to="/library" icon={Library} label="Library of Nix" current={current} />
          <ChildLinks items={LIBRARY_CHILDREN} currentFull={currentFull} />

          {user && (
            <>
              <Divider />
              {/* Worlds link + expandable world→campaign tree */}
              <SidebarLink to="/worlds" icon={Globe} label="Worlds" current={current} />
              <WorldsTree
                current={current}
                userId={viewAsUser?.userId ?? user.userId}
              />

              {/* Characters + child links */}
              <SidebarLink to="/characters" icon={Swords} label="Characters" current={current} />
              <ChildLinks items={CHARACTER_CHILDREN} currentFull={currentFull} />

              <SidebarLink to="/profile" icon={User} label="Profile" current={current} />
            </>
          )}

          {isDmOrAdmin && (
            <>
              <Divider />
              {DM_ITEMS.map((item) => (
                <SidebarLink key={item.to} {...item} current={current} />
              ))}
            </>
          )}

          {isAdmin && (
            <>
              {ADMIN_ITEMS.map((item) => (
                <SidebarLink key={item.to} {...item} current={current} />
              ))}
              <ChildLinks items={ADMIN_CHILDREN} currentFull={currentFull} />
            </>
          )}
        </nav>

        {/* "Support Darkfeld" — pinned to the bottom of the sidebar card, always visible */}
        <div className="p-2 border-t border-darkbg-border shrink-0">
          <SidebarLink to="/donate" icon={Heart} label="Support Darkfeld" current={current} />
          <ChildLinks items={DONATE_CHILDREN} currentFull={currentFull} />
        </div>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// WorldsTree — fetches worlds + campaigns once on mount; shows an expandable
// tree of worlds with their campaigns as navigation links.
// ---------------------------------------------------------------------------
function WorldsTree({ current, userId }: { current: string; userId: string }) {
  const location                          = useLocation();
  const currentSearch                     = location.search;
  const [entries, setEntries]             = useState<WorldEntry[]>([]);
  const [expanded, setExpanded]           = useState<Set<string>>(new Set());
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(new Set());

  // Fetch all worlds and their campaigns when the user session is established.
  // We re-run only when the logged-in user changes (login / account switch).
  useEffect(() => {
    let cancelled = false;

    apiFetch<{ worlds: World[] }>('/worlds')
      .then(({ worlds }) => {
        if (cancelled) return;

        // All worlds start expanded so campaigns are immediately visible.
        const initExpanded = new Set<string>(worlds.map((w) => w.worldId));

        // Fetch campaigns for every world in parallel; failures are silenced
        // so a single forbidden world doesn't break the whole tree.
        Promise.all(
          worlds.map((w) =>
            apiFetch<{ campaigns: Campaign[] }>(`/worlds/${w.worldId}/campaigns`)
              .then((r) => ({
                world: w,
                campaigns: [...r.campaigns].sort((a, b) => {
                  if (!b.startDate) return -1;
                  if (!a.startDate) return 1;
                  return b.startDate.localeCompare(a.startDate);
                }),
              }))
              .catch(() => ({ world: w, campaigns: [] as Campaign[] })),
          ),
        ).then((allEntries) => {
          if (cancelled) return;
          setEntries(allEntries);
          setExpanded(initExpanded);
        });
      })
      .catch(() => {/* silently ignore — sidebar tree is non-critical */});

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // When the user navigates to a campaign page that belongs to a collapsed
  // world, auto-expand that world so the active link is always visible.
  useEffect(() => {
    const match = current.match(/^\/campaigns\/([^/]+)/);
    if (!match) return;
    const campaignId = match[1]!;
    const parent = entries.find((e) =>
      e.campaigns.some((c) => c.campaignId === campaignId),
    );
    if (parent && !expanded.has(parent.world.worldId)) {
      setExpanded((prev) => new Set([...prev, parent.world.worldId]));
    }
  }, [current, entries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand the campaign sub-tabs when navigating to a campaign URL.
  useEffect(() => {
    const match = current.match(/^\/campaigns\/([^/?]+)/);
    if (!match) return;
    const cid = match[1]!;
    setExpandedCampaigns((prev) => {
      if (prev.has(cid)) return prev;
      return new Set([...prev, cid]);
    });
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  // Archived worlds are never shown in the sidebar for any role — they are only
  // visible in the Admin Panel worlds tab, which is the correct place to manage them.
  const visibleEntries = entries.filter((e) => e.world.status !== 'archived');

  if (visibleEntries.length === 0) return null;

  function toggleWorld(worldId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(worldId)) next.delete(worldId);
      else next.add(worldId);
      return next;
    });
  }

  function toggleCampaign(campaignId: string) {
    setExpandedCampaigns((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });
  }

  return (
    <div className="ml-2 space-y-0.5">
      {visibleEntries.map(({ world, campaigns }) => {
        const isExpanded       = expanded.has(world.worldId);
        const isWorldActive    = current.startsWith(`/worlds/${world.worldId}`);
        const hasCampaignHere  = campaigns.some((c) =>
          current.startsWith(`/campaigns/${c.campaignId}`),
        );
        const rowHighlight     = isWorldActive || hasCampaignHere;

        return (
          <div key={world.worldId}>
            {/* ── World row ── */}
            <div className="flex items-center gap-0.5">
              {/* Expand / collapse toggle */}
              <button
                onClick={() => toggleWorld(world.worldId)}
                className="flex items-center justify-center w-5 h-5 shrink-0
                           text-parchment-muted/30 hover:text-parchment-muted
                           transition-colors rounded"
                aria-label={isExpanded ? 'Collapse world' : 'Expand world'}
              >
                <ChevronRight
                  className={`w-3 h-3 transition-transform duration-150 ${
                    isExpanded ? 'rotate-90' : ''
                  }`}
                />
              </button>

              {/* World name → world detail page */}
              <Link
                to={`/worlds/${world.worldId}`}
                className={`flex-1 flex items-center gap-1.5 px-1.5 py-1.5 rounded
                            text-xs truncate transition-colors duration-150 ${
                  rowHighlight
                    ? 'text-gold'
                    : 'text-parchment-muted/60 hover:text-parchment hover:bg-darkbg-tertiary'
                }`}
              >
                <span className="truncate">{world.name}</span>
                {/* Dot indicating an active campaign is open in this world */}
                {hasCampaignHere && !isWorldActive && (
                  <span className="ml-auto w-1 h-1 rounded-full bg-gold/60 shrink-0" />
                )}
              </Link>
            </div>

            {/* ── Campaign links ── */}
            {isExpanded && (
              <div className="ml-5 mt-0.5 space-y-0.5">
                {campaigns.length === 0 ? (
                  <p className="px-2 py-1 text-xs text-parchment-muted/30 italic">
                    No campaigns
                  </p>
                ) : (
                  campaigns.map((campaign) => {
                    const cid               = campaign.campaignId;
                    const isOnCampaign      = current.startsWith(`/campaigns/${cid}`);
                    const isCampaignExpanded = expandedCampaigns.has(cid);
                    const activeTab         = isOnCampaign
                      ? (new URLSearchParams(currentSearch).get('tab') ?? 'overview')
                      : null;

                    return (
                      <div key={cid}>
                        {/* Campaign row: chevron toggle + name link */}
                        <div className="flex items-center gap-0.5">
                          <button
                            onClick={() => toggleCampaign(cid)}
                            className="flex items-center justify-center w-5 h-5 shrink-0
                                       text-parchment-muted/30 hover:text-parchment-muted
                                       transition-colors rounded"
                            aria-label={isCampaignExpanded ? 'Collapse campaign' : 'Expand campaign'}
                          >
                            <ChevronRight
                              className={`w-3 h-3 transition-transform duration-150 ${
                                isCampaignExpanded ? 'rotate-90' : ''
                              }`}
                            />
                          </button>

                          <Link
                            to={`/campaigns/${cid}`}
                            className={`flex-1 flex items-center gap-1.5 px-1.5 py-1.5 rounded
                                        text-xs truncate transition-colors duration-150 group ${
                              isOnCampaign
                                ? 'text-gold'
                                : 'text-parchment-muted/50 hover:text-parchment hover:bg-darkbg-tertiary'
                            }`}
                          >
                            <BookOpen
                              className={`w-3 h-3 shrink-0 transition-colors ${
                                isOnCampaign
                                  ? 'text-gold'
                                  : 'text-parchment-muted/30 group-hover:text-parchment-muted'
                              }`}
                            />
                            <span className="truncate flex-1">{campaign.name}</span>
                            <span
                              className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                STATUS_DOT[campaign.status] ?? 'bg-parchment-muted/20'
                              }`}
                              title={campaign.status.replace('_', ' ')}
                            />
                          </Link>
                        </div>

                        {/* Sub-tab links (collapsible) */}
                        {isCampaignExpanded && (
                          <div className="ml-5 mt-0.5 mb-1 pl-2 border-l border-darkbg-border/60 space-y-0.5">
                            {CAMPAIGN_TABS.map(({ id, label }) => {
                              const to        = id === 'overview' ? `/campaigns/${cid}` : `/campaigns/${cid}?tab=${id}`;
                              const isTabActive = activeTab === id;
                              return (
                                <Link
                                  key={id}
                                  to={to}
                                  className={`flex items-center gap-2 px-2 py-1 rounded text-xs transition-colors ${
                                    isTabActive
                                      ? 'text-gold bg-gold/5'
                                      : 'text-parchment-muted/50 hover:text-parchment hover:bg-darkbg-tertiary'
                                  }`}
                                >
                                  <span className={`w-1 h-1 rounded-full shrink-0 ${isTabActive ? 'bg-gold' : 'bg-parchment-muted/30'}`} />
                                  <span className="truncate">{label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChildLinks — renders an indented list of sub-navigation items beneath a
// parent SidebarLink. Active detection uses the full path+search string so
// query-param links (e.g. /admin?tab=feedback) are detected correctly.
//
// Style guideline: every top-level nav section that has distinct sub-pages
// or sub-tabs should expose those as ChildLinks directly in the sidebar so
// users can navigate in one click. Child links are always visible (no
// expand/collapse) to keep navigation immediately scannable.
// ---------------------------------------------------------------------------
function ChildLinks({ items, currentFull }: { items: ChildItem[]; currentFull: string }) {
  return (
    <div className="ml-3 mt-0.5 mb-1 pl-3 border-l border-darkbg-border/60 space-y-0.5">
      {items.map((item) => (
        <ChildLink key={item.to} {...item} currentFull={currentFull} />
      ))}
    </div>
  );
}

function ChildLink({ to, label, currentFull }: ChildItem & { currentFull: string }) {
  // Exact match on full path+search (handles both plain paths and ?tab= links).
  // Also handle prefix-based matching for paths that are hubs with sub-routes
  // (e.g. /library/website-resources highlights for any /library/website-resources/* URL).
  const plainTo = to.split('?')[0]!;
  const isActive =
    currentFull === to ||
    currentFull.startsWith(plainTo + '?tab=' + (to.split('tab=')[1] ?? '')) ||
    // Prefix match only for paths that don't end with a segment that could be a false positive.
    // Guard: only match if `to` has at least two path segments to avoid '/library' matching '/library/...'
    (plainTo.split('/').filter(Boolean).length >= 2 && currentFull.startsWith(plainTo + '/'));

  return (
    <Link
      to={to}
      className={`flex items-center gap-2 px-2 py-1 rounded text-xs transition-colors ${
        isActive
          ? 'text-gold bg-gold/5'
          : 'text-parchment-muted/50 hover:text-parchment hover:bg-darkbg-tertiary'
      }`}
    >
      <span className={`w-1 h-1 rounded-full shrink-0 ${isActive ? 'bg-gold' : 'bg-parchment-muted/30'}`} />
      <span className="truncate">{label}</span>
    </Link>
  );
}

function Divider() {
  return <div className="my-2 mx-1 border-t border-darkbg-border" />;
}

function SidebarLink({ to, icon: Icon, label, current }: NavItem & { current: string }) {
  const isActive = current === to || current.startsWith(to + '/');

  return (
    <Link
      to={to}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm
                  transition-all duration-150 group ${
                    isActive
                      ? 'bg-gold/10 text-gold font-medium'
                      : 'text-parchment-muted hover:text-parchment hover:bg-darkbg-tertiary'
                  }`}
    >
      <Icon
        className={`w-4 h-4 shrink-0 transition-colors duration-150 ${
          isActive
            ? 'text-gold'
            : 'text-parchment-muted/50 group-hover:text-parchment-muted'
        }`}
      />
      <span className="truncate">{label}</span>
      {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-gold shrink-0" />}
    </Link>
  );
}
